/**
 * SingleSlideout — the CRM record in the body-portaled `SlideoutMenu`.
 *
 * Layout-managed: the chrome renders the header and footer SLOTS (and its own
 * close button, inline at the end of the header row) in every state;
 * `RecordSurfaceProvider` resolves the record once, above the chrome, and the
 * regions in `record-slideout-regions` switch on that state. Nothing the
 * record does — loading, 403, 404, fetch error — can take the close button
 * with it. See docs/claude/REACT_19_PATTERNS.md § 1.
 *
 * This component owns the record's DATA (`RecordSurfaceProvider`) and its
 * DISMISSAL (close animation, unsaved-changes guard); `RecordSlideoutChrome`
 * owns the panel. For hosting the same regions as a real flex sibling (no
 * portal), see `InlineCrmObjectPanel`.
 */

import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSyncExternalStore } from 'react';

import { useSlideoutWidth } from '@/contexts/SlideoutWidthContext';

import {
  getObjectByPk,
  resolveEntityId,
  subscribeToStoredObject,
} from '../store/crmObjectStore';
import { RecordSurfaceProvider } from './contexts/RecordSurfaceContext';
import { useGuardedDismiss } from './hooks/useGuardedDismiss';
import { RecordSlideoutChrome } from './record-slideout-chrome';
// `getEntityTypeConfig` supplies the dialog's accessible name.
import { getEntityTypeConfig } from './registry/entityTypeRegistry';

export interface SingleSlideoutProps {
  objectId: string;
  minWidth: number;
  maxWidth: number;
  onClose: () => void;
  onBack?: () => void;
  showBackButton?: boolean;
  onGuardChange?: (guard: ((action: () => void) => boolean) | null) => void;
}

export function SingleSlideout({
  objectId,
  minWidth,
  maxWidth,
  onClose,
  onBack,
  showBackButton,
  onGuardChange,
}: SingleSlideoutProps): ReactElement {
  const { width, setWidth, setIsResizing } = useSlideoutWidth();

  // Close animation: play the 500ms exit before actually popping the stack.
  const [isClosing, setIsClosing] = useState(false);
  const closingTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const animateAndClose = useCallback(() => {
    if (isClosing) {
      return;
    }
    setIsClosing(true);
    closingTimerRef.current = setTimeout(() => {
      onClose();
    }, 500);
  }, [onClose, isClosing]);

  useEffect(() => {
    return () => {
      if (closingTimerRef.current) {
        clearTimeout(closingTimerRef.current);
      }
    };
  }, []);

  // Every dismissal path — the chrome's close button, Escape, the header's
  // back chevron — goes through the unsaved-changes guard the surface
  // reports; the guard is also forwarded to the host (CrmObjectSlideout
  // registers it as the stack push guard).
  const { close, back, handleGuardChange } = useGuardedDismiss({
    onClose: animateAndClose,
    onBack,
    onGuardChange,
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close();
      }
    },
    [close]
  );

  const handleWidthChange = useCallback(
    (newWidth: number) => {
      setWidth(newWidth);
    },
    [setWidth]
  );

  // Specific accessible name for the slideout dialog, so screen readers
  // announce the actual record instead of the generic "Slideout panel"
  // fallback. Derived from the store entry (summary or complete) — falls
  // back to just the entity type's display name while nothing is stored yet.
  const resolvedId =
    getObjectByPk(objectId)?.entity_id ?? resolveEntityId(objectId);
  const { subscribe: labelSubscribe, getSnapshot: labelGetSnapshot } = useMemo(
    () => subscribeToStoredObject(resolvedId),
    [resolvedId]
  );
  const storedForLabel = useSyncExternalStore(labelSubscribe, labelGetSnapshot);
  const dialogAriaLabel = useMemo(() => {
    const data = storedForLabel?.data;
    const entityConfig = getEntityTypeConfig(data?.entity_type_name ?? '');
    return data
      ? `${entityConfig.getDisplayName(data)} (${entityConfig.displayName})`
      : entityConfig.displayName;
  }, [storedForLabel?.data]);

  return (
    <RecordSurfaceProvider
      objectId={objectId}
      onGuardChange={handleGuardChange}
    >
      <RecordSlideoutChrome
        isOpen={!isClosing}
        onOpenChange={handleOpenChange}
        onBack={showBackButton ? back : undefined}
        dialogAriaLabel={dialogAriaLabel}
        initialWidth={width}
        minWidth={minWidth}
        maxWidth={maxWidth}
        onWidthChange={handleWidthChange}
        onResizingChange={setIsResizing}
      />
    </RecordSurfaceProvider>
  );
}
